// src/components/StorageGate.tsx
// Full-screen over-quota gate (matches iOS StorageGateView / Android StorageGateActivity).
// Shown when enforcement.state === 'locked'. Two ways out: free up space or buy storage.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HardDrive, FolderOpen, Plus, LogOut } from "lucide-react";
import { apiService } from "../services/api";
import { useAuth } from "../context/AuthContext";

const GB = 1_073_741_824;
const fmtGb = (bytes: number) => (bytes / GB).toFixed(1);

export default function StorageGate({ enforcement }: { enforcement: any }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const used = enforcement?.usedBytes ?? 0;
  const quota = enforcement?.quotaBytes ?? 0;
  const excess = enforcement?.excessBytes ?? Math.max(0, used - quota);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-5">
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-6">
          <HardDrive size={36} className="text-red-400" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Storage Full</h1>
        <p className="text-gray-400 mb-1">
          You're using <span className="text-white font-medium">{fmtGb(used)}GB</span> of{" "}
          <span className="text-white font-medium">{fmtGb(quota)}GB</span>.
        </p>
        <p className="text-gray-400 mb-6">
          You're <span className="text-red-400 font-medium">{fmtGb(excess)}GB</span> over your limit.
          Free up space or upgrade your plan to keep using Vastavik.
        </p>

        <button
          onClick={() => navigate("/pricing")}
          className="w-full py-3 mb-3 rounded-xl bg-blue-600 hover:bg-blue-500 font-semibold flex items-center justify-center gap-2"
        >
          <Plus size={18} /> Upgrade Plan
        </button>
        <button
          onClick={() => navigate("/library")}
          className="w-full py-3 mb-3 rounded-xl bg-white/10 hover:bg-white/20 font-semibold flex items-center justify-center gap-2"
        >
          <FolderOpen size={18} /> Manage Library
        </button>
        <button
          onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
          className="w-full py-3 rounded-xl text-gray-400 hover:text-white flex items-center justify-center gap-2"
        >
          <LogOut size={18} /> Log Out
        </button>
      </div>
    </div>
  );
}

/**
 * Wraps protected content: checks enforcement once and, if the account is LOCKED
 * (over quota past the grace period), shows the gate instead of the page — except
 * on the Storage page itself (so the user can buy more) and the Library (free up space).
 */
export function StorageGateGuard({ children }: { children: React.ReactNode }) {
  const [enforcement, setEnforcement] = useState<any>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let alive = true;
    apiService
      .getStorageEnforcement()
      .then((e) => { if (alive) setEnforcement(e); })
      .catch(() => { /* on error, don't gate */ })
      .finally(() => { if (alive) setChecked(true); });
    return () => { alive = false; };
  }, []);

  if (!checked) return <>{children}</>;

  const locked = enforcement?.state === "locked";
  const path = window.location.pathname;
  const allowWhileLocked =
    path.startsWith("/storage") ||
    path.startsWith("/pricing") ||
    path.startsWith("/library") ||
    path.startsWith("/profile");

  if (locked && !allowWhileLocked) return <StorageGate enforcement={enforcement} />;
  return <>{children}</>;
}
